import React, { useEffect, useRef, useState } from 'react';
import { Play, Loader2, Check, Sparkles } from 'lucide-react';
import { adminListVoices, adminPreviewVoice, type VoiceOption } from '../../services/adminService';

type Gender = 'female' | 'male' | 'neutral' | undefined;

/**
 * Voice picker for the admin agent editor — a gender-filtered pool of named voices, each with a play-to-preview
 * (a short sample synthesized in that voice), plus an "Auto" option that clears the override so the voice is
 * picked to match the agent's gender. Monochrome + Lucide + tap-friendly, per the house style.
 */
const AgentVoicePicker: React.FC<{
  gender: Gender;
  voiceId?: string | null;
  quality?: 'standard' | 'premium';
  onVoice: (id: string | null) => void;
  onQuality: (q: 'standard' | 'premium') => void;
}> = ({ gender, voiceId, quality = 'standard', onVoice, onQuality }) => {
  const [voices, setVoices] = useState<{ female: VoiceOption[]; male: VoiceOption[] } | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [previewErr, setPreviewErr] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => { adminListVoices().then(setVoices).catch(() => setVoices({ female: [], male: [] })); }, []);
  // Stop any in-flight preview when the editor closes so it can't keep playing after unmount.
  useEffect(() => () => { try { audioRef.current?.pause(); } catch { /* ignore */ } }, []);

  const preview = async (id: string) => {
    setPreviewing(id); setPreviewErr('');
    let audio = audioRef.current;
    if (!audio) { audio = new Audio(); audioRef.current = audio; }
    try { audio.src = ''; audio.muted = true; void audio.play().catch(() => { /* prime the element in the gesture */ }); } catch { /* ignore */ }
    try {
      const { audio: dataUrl } = await adminPreviewVoice(id, quality);
      audio.muted = false; audio.src = dataUrl;
      audio.onended = () => setPreviewing(null);
      audio.onerror = () => { setPreviewing(null); setPreviewErr('Could not play that voice — try again.'); };
      await audio.play();
    } catch { setPreviewing(null); setPreviewErr('Voice preview is unavailable right now.'); }
  };

  // Show the pool(s) that match the agent's gender; a neutral/unset agent sees both so it's never empty.
  const groups: Array<{ label: string; items: VoiceOption[] }> = [];
  if (voices) {
    if (gender !== 'male') groups.push({ label: 'Female voices', items: voices.female });
    if (gender !== 'female') groups.push({ label: 'Male voices', items: voices.male });
  }

  const Row: React.FC<{ v: VoiceOption }> = ({ v }) => {
    const selected = voiceId === v.id;
    return (
      <div className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${selected ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
        <button type="button" onClick={() => preview(v.id)} aria-label={`Preview ${v.label}`} title="Preview"
          className="p-1.5 rounded hover:bg-black/10 dark:hover:bg-white/10">
          {previewing === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
        </button>
        <button type="button" onClick={() => onVoice(v.id)} className="flex-1 text-left text-sm truncate">{v.label}</button>
        {selected && <Check className="w-3.5 h-3.5" />}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-secondary-200 dark:border-secondary-800 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-secondary-500">Voice</p>
        <div className="inline-flex rounded-md border border-secondary-200 dark:border-secondary-700 overflow-hidden text-xs">
          {(['standard', 'premium'] as const).map((q) => (
            <button key={q} type="button" onClick={() => onQuality(q)}
              className={`px-2 py-1 ${quality === q ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500'}`}>{q}</button>
          ))}
        </div>
      </div>

      <button type="button" onClick={() => onVoice(null)}
        className={`mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs ${!voiceId ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'border border-secondary-200 dark:border-secondary-700 text-secondary-500'}`}>
        <Sparkles className="w-3 h-3" /> Auto · match gender
      </button>

      {previewErr && <p className="mt-1.5 text-[11px] text-rose-500">{previewErr}</p>}

      {!voices ? (
        <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-secondary-400" /></div>
      ) : groups.every((g) => g.items.length === 0) ? (
        <p className="mt-2 text-xs text-secondary-400">No voices available right now.</p>
      ) : (
        <div className="mt-2 max-h-56 overflow-auto space-y-2">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="text-[11px] uppercase tracking-wide text-secondary-400 mb-1">{g.label}</div>
              <div className="space-y-0.5">{g.items.map((v) => <Row key={v.id} v={v} />)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentVoicePicker;
